import {
  TrackingPageBlock,
  TrackingPageBlockType,
} from "@redotech/order-tracking-common/tracking-page-definition";
import { isFramedBlock } from "@redotech/redo-block-service-common/block-content";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface LiveBlockState {
  selectedBlockId: string | null;
  /** The selected block, unless the pointer is busy; its frame takes real input. */
  liveBlockId: string | null;
}

/** Absent outside the builder: nothing is selected or live. */
export const LiveBlockContext = createContext<LiveBlockState>({
  selectedBlockId: null,
  liveBlockId: null,
});

const ReportPointerBusyContext = createContext<(busy: boolean) => void>(
  () => {},
);

export function isFramedCustomHtmlBlock(block: TrackingPageBlock): boolean {
  return (
    block.type === TrackingPageBlockType.CUSTOM_HTML && isFramedBlock(block)
  );
}

export function useBlockLiveState(blockId: string): {
  live: boolean;
  selected: boolean;
} {
  const { selectedBlockId, liveBlockId } = useContext(LiveBlockContext);
  return {
    live: liveBlockId === blockId,
    selected: selectedBlockId === blockId,
  };
}

/** Marks the pointer busy (drag, resize, drop) while `busy` is true. */
export function useReportPointerBusy(busy: boolean): void {
  const report = useContext(ReportPointerBusyContext);
  useEffect(() => {
    if (!busy) {
      return;
    }
    report(true);
    return () => report(false);
  }, [busy, report]);
}

/** The selected block is live unless the pointer is busy. */
export function LiveBlockProvider({
  selectedBlockId,
  children,
}: {
  selectedBlockId: string | null;
  children: ReactNode;
}) {
  const [busyCount, setBusyCount] = useState(0);
  const report = useCallback(
    (busy: boolean) => setBusyCount((count) => count + (busy ? 1 : -1)),
    [],
  );
  const state = useMemo(
    () => ({
      selectedBlockId,
      liveBlockId: busyCount > 0 ? null : selectedBlockId,
    }),
    [selectedBlockId, busyCount],
  );

  return (
    <ReportPointerBusyContext.Provider value={report}>
      <LiveBlockContext.Provider value={state}>
        {children}
      </LiveBlockContext.Provider>
    </ReportPointerBusyContext.Provider>
  );
}
